import React, { useEffect, useState, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getVisibleRutas, isRutaRestricted } from '../lib/rutas';
import {
  CheckCircle2, XCircle, Clock, Filter, X, Eye, ChevronDown, ChevronUp,
  AlertTriangle, Image as ImageIcon, ExternalLink, FileText, User, Calendar,
  Search, DollarSign, Banknote
} from 'lucide-react';

interface PaymentTransaction {
  id: string;
  amount: number;
  late_fee: number;
  bank_name: string | null;
  proof_file_url: string[] | null;
  payment_date_time: string | null;
  registered_at: string;
  registered_by: string;
  status: 'pending_verification' | 'approved' | 'rejected';
  verified_at: string | null;
  verified_by: string | null;
  rejection_reason: string | null;
  allocation_source_type: string;
  allocation_metadata: Record<string, unknown>;
  client_name: string;
  contract_code: string;
  contract_id: string;
  ruta: number;
  installment_due_date: string;
  seller_email: string;
}

type TabFilter = 'pending_verification' | 'approved' | 'rejected' | 'all';

export default function PixControl() {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabFilter>('pending_verification');
  const [selectedRuta, setSelectedRuta] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'cashbox' | 'today' | 'week' | 'month' | 'all'>('cashbox');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [openCashboxes, setOpenCashboxes] = useState<{ ruta: number; opened_at: string }[]>([]);
  const [cashboxesLoaded, setCashboxesLoaded] = useState(false);

  useEffect(() => {
    if (user?.unit_id) fetchOpenCashboxes();
  }, [user]);

  useEffect(() => {
    fetchTransactions();
  }, [user, activeTab, dateRange, openCashboxes, cashboxesLoaded]);

  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeTab, selectedRuta, searchQuery, dateRange]);

  useEffect(() => {
    if (isRutaRestricted(user)) {
      const visible = getVisibleRutas(user);
      if (!visible.includes(selectedRuta)) setSelectedRuta(visible[0]);
    }
  }, [user]);

  const fetchTransactions = async () => {
    if (!user) return;
    if (dateRange === 'cashbox' && !cashboxesLoaded) return;
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('payment_transactions')
        .select(`
          id,
          amount,
          late_fee,
          bank_name,
          proof_file_url,
          payment_date_time,
          registered_at,
          registered_by,
          status,
          verified_at,
          verified_by,
          rejection_reason,
          allocation_source_type,
          allocation_metadata,
          contract_id,
          installment:installments!inner(
            due_date,
            contract:contracts!inner(
              client_name,
              code,
              ruta
            )
          ),
          registrant:registered_by(email)
        `)
        .eq('unit_id', user.unit_id)
        .order('registered_at', { ascending: false });

      if (activeTab !== 'all') {
        query = query.eq('status', activeTab);
      }

      // Date filter
      const now = new Date();
      if (dateRange === 'cashbox') {
        if (openCashboxes.length > 0) {
          const earliest = openCashboxes.reduce(
            (min, c) => (c.opened_at < min ? c.opened_at : min),
            openCashboxes[0].opened_at
          );
          query = query.gte('registered_at', earliest);
        }
      } else if (dateRange === 'today') {
        const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        query = query.gte('registered_at', start.toISOString());
      } else if (dateRange === 'week') {
        const start = new Date(now);
        start.setDate(start.getDate() - 7);
        query = query.gte('registered_at', start.toISOString());
      } else if (dateRange === 'month') {
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        query = query.gte('registered_at', start.toISOString());
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      const mapped: PaymentTransaction[] = (data || []).map((t: any) => ({
        id: t.id,
        amount: t.amount,
        late_fee: t.late_fee || 0,
        bank_name: t.bank_name,
        proof_file_url: t.proof_file_url,
        payment_date_time: t.payment_date_time,
        registered_at: t.registered_at,
        registered_by: t.registered_by,
        status: t.status,
        verified_at: t.verified_at,
        verified_by: t.verified_by,
        rejection_reason: t.rejection_reason,
        allocation_source_type: t.allocation_source_type || 'regular_payment',
        allocation_metadata: t.allocation_metadata || {},
        client_name: t.installment?.contract?.client_name || 'N/A',
        contract_code: t.installment?.contract?.code || '',
        contract_id: t.contract_id,
        ruta: t.installment?.contract?.ruta || 0,
        installment_due_date: t.installment?.due_date || '',
        seller_email: (t.registrant as any)?.email || '',
      }));

      setTransactions(mapped);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setError('Erro ao carregar transacoes de pagamento');
    } finally {
      setLoading(false);
    }
  };

  const fetchOpenCashboxes = async () => {
    if (!user?.unit_id) return;
    const { data, error: cbError } = await supabase
      .from('cashboxes')
      .select('ruta, opened_at')
      .eq('unit_id', user.unit_id)
      .eq('status', 'open');
    if (cbError) {
      console.error('Error fetching open cashboxes:', cbError);
      return;
    }
    setOpenCashboxes((data as { ruta: number; opened_at: string }[]) || []);
    setCashboxesLoaded(true);
  };

  const scopedTransactions = useMemo(() => {
    let result = transactions;
    if (selectedRuta > 0) {
      result = result.filter(t => t.ruta === selectedRuta);
    }
    if (dateRange === 'cashbox') {
      result = result.filter(t => {
        const cb = openCashboxes.find(c => c.ruta === t.ruta);
        return cb && new Date(t.registered_at) >= new Date(cb.opened_at);
      });
    }
    return result;
  }, [transactions, selectedRuta, dateRange, openCashboxes]);

  const filteredTransactions = useMemo(() => {
    let result = scopedTransactions;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.client_name.toLowerCase().includes(q) ||
        t.contract_code.toLowerCase().includes(q) ||
        t.seller_email.toLowerCase().includes(q)
      );
    }
    return result;
  }, [scopedTransactions, searchQuery]);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, total: 0 };
    for (const t of scopedTransactions) {
      if (t.status === 'pending_verification') c.pending++;
      else if (t.status === 'approved') c.approved++;
      else if (t.status === 'rejected') c.rejected++;
      c.total++;
    }
    return c;
  }, [scopedTransactions]);

  const totalPendingAmount = useMemo(() => {
    return filteredTransactions
      .filter(t => t.status === 'pending_verification')
      .reduce((sum, t) => sum + t.amount + t.late_fee, 0);
  }, [filteredTransactions]);

  const handleApprove = async (transactionId: string) => {
    if (!user) return;
    setActionLoading(transactionId);
    try {
      const { error: updateError } = await supabase
        .from('payment_transactions')
        .update({
          status: 'approved',
          verified_by: user.id,
          verified_at: new Date().toISOString(),
        })
        .eq('id', transactionId);

      if (updateError) throw updateError;

      setTransactions(prev =>
        prev.map(t =>
          t.id === transactionId
            ? { ...t, status: 'approved' as const, verified_at: new Date().toISOString(), verified_by: user.id }
            : t
        )
      );

      if (expandedId === transactionId) setExpandedId(null);
    } catch (err) {
      console.error('Error approving:', err);
      alert('Erro ao aprovar pagamento');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (transactionId: string, reasonArg?: string) => {
    const reason = (reasonArg ?? rejectionReason).trim();
    if (!user || !reason) {
      alert('Informe o motivo da recusa');
      return;
    }
    setActionLoading(transactionId);
    try {
      const { error: updateError } = await supabase
        .from('payment_transactions')
        .update({
          status: 'rejected',
          verified_by: user.id,
          verified_at: new Date().toISOString(),
          rejection_reason: reason,
        })
        .eq('id', transactionId);

      if (updateError) throw updateError;

      setTransactions(prev =>
        prev.map(t =>
          t.id === transactionId
            ? {
                ...t,
                status: 'rejected' as const,
                verified_at: new Date().toISOString(),
                verified_by: user.id,
                rejection_reason: reason,
              }
            : t
        )
      );

      setRejectionReason('');
      if (expandedId === transactionId) setExpandedId(null);
    } catch (err) {
      console.error('Error rejecting:', err);
      alert('Erro ao recusar pagamento');
    } finally {
      setActionLoading(null);
    }
  };

  const promptReject = (transactionId: string) => {
    const reason = window.prompt('Motivo da recusa:');
    if (reason === null) return;
    if (!reason.trim()) {
      alert('Informe o motivo da recusa');
      return;
    }
    handleReject(transactionId, reason.trim());
  };

  const pendingFiltered = useMemo(
    () => filteredTransactions.filter(t => t.status === 'pending_verification'),
    [filteredTransactions]
  );

  const selectedCount = useMemo(
    () => pendingFiltered.filter(t => selectedIds.has(t.id)).length,
    [pendingFiltered, selectedIds]
  );

  const allPendingSelected = pendingFiltered.length > 0 && selectedCount === pendingFiltered.length;

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (prev.size > 0 && pendingFiltered.every(t => prev.has(t.id))) return new Set();
      return new Set(pendingFiltered.map(t => t.id));
    });
  };

  const bulkApprove = async () => {
    if (!user) return;
    const ids = pendingFiltered.filter(t => selectedIds.has(t.id)).map(t => t.id);
    if (ids.length === 0) return;
    if (!confirm(`Aprovar ${ids.length} pagamento(s) selecionado(s)?`)) return;
    setBulkLoading(true);
    try {
      const verifiedAt = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('payment_transactions')
        .update({ status: 'approved', verified_by: user.id, verified_at: verifiedAt })
        .in('id', ids);
      if (updateError) throw updateError;
      setTransactions(prev =>
        prev.map(t =>
          ids.includes(t.id)
            ? { ...t, status: 'approved' as const, verified_at: verifiedAt, verified_by: user.id }
            : t
        )
      );
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Error bulk approving:', err);
      alert('Erro ao aprovar pagamentos');
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkReject = async () => {
    if (!user) return;
    const ids = pendingFiltered.filter(t => selectedIds.has(t.id)).map(t => t.id);
    if (ids.length === 0) return;
    const reason = window.prompt(`Motivo da recusa para ${ids.length} pagamento(s):`);
    if (reason === null) return;
    if (!reason.trim()) {
      alert('Informe o motivo da recusa');
      return;
    }
    setBulkLoading(true);
    try {
      const verifiedAt = new Date().toISOString();
      const { error: updateError } = await supabase
        .from('payment_transactions')
        .update({ status: 'rejected', verified_by: user.id, verified_at: verifiedAt, rejection_reason: reason.trim() })
        .in('id', ids);
      if (updateError) throw updateError;
      setTransactions(prev =>
        prev.map(t =>
          ids.includes(t.id)
            ? { ...t, status: 'rejected' as const, verified_at: verifiedAt, verified_by: user.id, rejection_reason: reason.trim() }
            : t
        )
      );
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Error bulk rejecting:', err);
      alert('Erro ao recusar pagamentos');
    } finally {
      setBulkLoading(false);
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });

  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });

  const tabs: { key: TabFilter; label: string; count: number; color: string }[] = [
    { key: 'pending_verification', label: 'Pendentes', count: counts.pending, color: 'amber' },
    { key: 'approved', label: 'Aprovados', count: counts.approved, color: 'emerald' },
    { key: 'rejected', label: 'Recusados', count: counts.rejected, color: 'red' },
    { key: 'all', label: 'Todos', count: counts.total, color: 'gray' },
  ];

  if (loading && transactions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-3">
        <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Carregando pagamentos...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Controle de Pagamentos</h1>
          <p className="text-sm text-gray-500 mt-0.5">Verifique e aprove as baixas de pagamento registradas pelos vendedores</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Pendentes</p>
                <p className="text-xl font-bold text-amber-600">{counts.pending}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Aprovados</p>
                <p className="text-xl font-bold text-emerald-600">{counts.approved}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <XCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Recusados</p>
                <p className="text-xl font-bold text-red-600">{counts.rejected}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">Pendente R$</p>
                <p className="text-lg font-bold text-blue-600 leading-tight">{formatCurrency(totalPendingAmount)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Filtros</span>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <div className="flex flex-col gap-1.5 min-w-[130px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Ruta</label>
              <select
                value={selectedRuta}
                onChange={(e) => setSelectedRuta(parseInt(e.target.value))}
                className="h-10 pl-3 pr-8 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {!isRutaRestricted(user) && <option value={0}>Todas</option>}
                {getVisibleRutas(user).map(r => <option key={r} value={r}>Ruta {r}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-1.5 min-w-[130px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Periodo</label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as typeof dateRange)}
                className="h-10 pl-3 pr-8 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="cashbox">Caixa aberto</option>
                <option value="today">Hoje</option>
                <option value="week">Ultima semana</option>
                <option value="month">Este mes</option>
                <option value="all">Todos</option>
              </select>
              {dateRange === 'cashbox' && (
                <span className="text-[11px] text-gray-400 leading-tight">
                  Mostra os pagamentos desde a abertura do caixa de cada ruta, igual ao painel do caixa.
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1.5 flex-1 min-w-[180px]">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Buscar</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Nome, codigo ou vendedor..."
                  className="h-10 w-full pl-9 pr-3 border border-gray-200 rounded-xl text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap ${
                activeTab === tab.key
                  ? tab.color === 'amber'
                    ? 'bg-amber-100 text-amber-700'
                    : tab.color === 'emerald'
                    ? 'bg-emerald-100 text-emerald-700'
                    : tab.color === 'red'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-gray-200 text-gray-700'
                  : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {tab.label}
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                activeTab === tab.key
                  ? tab.color === 'amber'
                    ? 'bg-amber-200 text-amber-800'
                    : tab.color === 'emerald'
                    ? 'bg-emerald-200 text-emerald-800'
                    : tab.color === 'red'
                    ? 'bg-red-200 text-red-800'
                    : 'bg-gray-300 text-gray-800'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Bulk actions bar */}
        {pendingFiltered.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allPendingSelected}
                onChange={toggleSelectAll}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-sm font-semibold text-gray-700">
                {selectedCount > 0
                  ? `${selectedCount} de ${pendingFiltered.length} selecionado(s)`
                  : 'Selecionar todos os pendentes'}
              </span>
            </label>

            {selectedCount > 0 && (
              <div className="flex items-center gap-2 sm:ml-auto">
                <button
                  onClick={bulkApprove}
                  disabled={bulkLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {bulkLoading ? 'Processando...' : 'Aprovar selecionados'}
                </button>
                <button
                  onClick={bulkReject}
                  disabled={bulkLoading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-sm transition-colors disabled:opacity-50"
                >
                  <XCircle className="w-4 h-4" />
                  Recusar selecionados
                </button>
              </div>
            )}
          </div>
        )}

        {/* Transactions list */}
        {filteredTransactions.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm py-16 flex flex-col items-center gap-3 text-center px-6">
            <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center">
              <FileText className="w-7 h-7 text-gray-400" />
            </div>
            <p className="text-gray-700 font-semibold">Nenhum pagamento encontrado</p>
            <p className="text-sm text-gray-400 max-w-xs">
              {dateRange === 'cashbox' && selectedRuta > 0 && !openCashboxes.some(c => c.ruta === selectedRuta)
                ? `Nao ha caixa aberto para a Ruta ${selectedRuta}. Abra o caixa ou escolha outro periodo.`
                : `Nao ha pagamentos ${activeTab === 'pending_verification' ? 'pendentes de verificacao' : activeTab === 'approved' ? 'aprovados' : activeTab === 'rejected' ? 'recusados' : ''} para os filtros selecionados.`}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredTransactions.map(tx => {
              const isExpanded = expandedId === tx.id;
              const isAllocation = tx.allocation_source_type !== 'regular_payment';

              return (
                <div
                  key={tx.id}
                  className={`bg-white rounded-2xl border shadow-sm overflow-hidden transition-colors ${
                    tx.status === 'pending_verification'
                      ? 'border-amber-200'
                      : tx.status === 'rejected'
                      ? 'border-red-200'
                      : 'border-gray-100'
                  }`}
                >
                  {/* Row summary */}
                  <div className="flex flex-wrap items-center gap-3 px-5 py-4">
                    {tx.status === 'pending_verification' && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(tx.id)}
                        onChange={() => toggleSelect(tx.id)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0 cursor-pointer"
                      />
                    )}

                    <button
                      onClick={() => setExpandedId(isExpanded ? null : tx.id)}
                      className="flex items-center gap-3 text-left flex-1 min-w-0 hover:opacity-80 transition-opacity"
                    >
                      {/* Status icon */}
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        tx.status === 'pending_verification'
                          ? 'bg-amber-100'
                          : tx.status === 'approved'
                          ? 'bg-emerald-100'
                          : 'bg-red-100'
                      }`}>
                        {tx.status === 'pending_verification'
                          ? <Clock className="w-4 h-4 text-amber-600" />
                          : tx.status === 'approved'
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          : <XCircle className="w-4 h-4 text-red-600" />
                        }
                      </div>

                      {/* Client info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-bold text-gray-900 truncate">{tx.client_name}</p>
                          {tx.contract_code && (
                            <span className="text-xs text-gray-400 font-mono">{tx.contract_code}</span>
                          )}
                          {isAllocation && (
                            <span className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                              Alocacao
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400 flex-wrap">
                          <span className="inline-flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {tx.seller_email ? tx.seller_email.split('@')[0] : 'N/A'}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            Ruta {tx.ruta}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDateTime(tx.registered_at)}
                          </span>
                        </div>
                      </div>

                      {/* Amount */}
                      <div className="text-right flex-shrink-0">
                        <p className="text-base font-bold text-gray-900">{formatCurrency(tx.amount)}</p>
                        {tx.late_fee > 0 && (
                          <p className="text-xs text-amber-600 font-semibold">+{formatCurrency(tx.late_fee)} juros</p>
                        )}
                      </div>
                    </button>

                    {/* Inline actions for pending */}
                    {tx.status === 'pending_verification' && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleApprove(tx.id)}
                          disabled={actionLoading === tx.id || bulkLoading}
                          title="Aprovar pagamento"
                          className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          <span className="hidden sm:inline">{actionLoading === tx.id ? '...' : 'Aprovar'}</span>
                        </button>
                        <button
                          onClick={() => promptReject(tx.id)}
                          disabled={actionLoading === tx.id || bulkLoading}
                          title="Recusar pagamento"
                          className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold text-sm transition-colors disabled:opacity-50"
                        >
                          <XCircle className="w-4 h-4" />
                          <span className="hidden sm:inline">Recusar</span>
                        </button>
                      </div>
                    )}

                    {/* Expand toggle */}
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : tx.id)}
                      className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                      title={isExpanded ? 'Recolher' : 'Ver detalhes'}
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="border-t border-gray-100 px-5 py-4 space-y-4 bg-gray-50/40">
                      {/* Detail grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Valor</p>
                          <p className="font-semibold text-gray-900">{formatCurrency(tx.amount)}</p>
                        </div>
                        {tx.late_fee > 0 && (
                          <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Juros</p>
                            <p className="font-semibold text-amber-600">{formatCurrency(tx.late_fee)}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Total</p>
                          <p className="font-semibold text-emerald-600">{formatCurrency(tx.amount + tx.late_fee)}</p>
                        </div>
                        {tx.bank_name && (
                          <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Banco</p>
                            <p className="font-semibold text-gray-900">{tx.bank_name}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Vencimento Parcela</p>
                          <p className="font-semibold text-gray-900">{tx.installment_due_date ? formatDate(tx.installment_due_date) : 'N/A'}</p>
                        </div>
                        {tx.payment_date_time && (
                          <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Data Pagamento</p>
                            <p className="font-semibold text-gray-900">{formatDateTime(tx.payment_date_time)}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Registrado por</p>
                          <p className="font-semibold text-gray-900">{tx.seller_email ? tx.seller_email.split('@')[0] : 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Data Registro</p>
                          <p className="font-semibold text-gray-900">{formatDateTime(tx.registered_at)}</p>
                        </div>
                        {tx.verified_at && (
                          <div>
                            <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">
                              {tx.status === 'approved' ? 'Aprovado em' : 'Recusado em'}
                            </p>
                            <p className="font-semibold text-gray-900">{formatDateTime(tx.verified_at)}</p>
                          </div>
                        )}
                      </div>

                      {/* Rejection reason */}
                      {tx.status === 'rejected' && tx.rejection_reason && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                          <p className="text-xs text-red-500 uppercase tracking-wide font-semibold mb-1">Motivo da recusa</p>
                          <p className="text-sm text-red-700">{tx.rejection_reason}</p>
                        </div>
                      )}

                      {/* Proof images */}
                      {tx.proof_file_url && tx.proof_file_url.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wide font-semibold mb-2 flex items-center gap-1.5">
                            <ImageIcon className="w-3.5 h-3.5" />
                            Comprovantes ({tx.proof_file_url.length})
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {tx.proof_file_url.map((url, idx) => (
                              <a
                                key={idx}
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative w-20 h-20 rounded-xl border-2 border-gray-200 overflow-hidden hover:border-blue-400 transition-colors bg-gray-100"
                              >
                                <img
                                  src={url}
                                  alt={`Comprovante ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLImageElement).style.display = 'none';
                                  }}
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                  <ExternalLink className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
